/**
 * Tag Recovery (04 §2.2): a ranger killed on an operation leaves his dog tags on the ground.
 * The next mission the company can reach in the same theatre gets his marker (the generator
 * places it from `params.tagRecovery`). Bring the tags home and his medals are confirmed on
 * the wall, and one of his perks can be taught to a living ranger. One chance: the marker
 * goes once that mission has been flown, whatever came of it, or when the company passes it by
 * (`refreshNodes` drops the marker of a skipped node).
 */

import { Pcg32 } from '../sim/rng';
import { redealChoices } from './aftermath/merit';
import { PERKS } from './content/perks';
import { clearTagMarker, openNodes } from './map';
import { companyRanger, findNode, type FullSummary, fullName, type OperationState, type ProfileState } from './state';
import type { Aftermath, PerkId, RangerRecord, TheatreNode } from './types';

const unresolved = (n: TheatreNode): boolean => n.state === 'hidden' || n.state === 'scouted' || n.state === 'open';

/**
 * Mark where `fallen`'s tags lie: the nearest mission ahead of the company in `theatre`, a
 * seeded pick when several are equally near. Nothing is marked when the theatre has none left.
 */
export function markTagRecovery(op: OperationState, fallen: RangerRecord, theatre: number): TheatreNode | null {
  const seen = new Set<string>();
  let frontier = openNodes(op).filter((n) => n.theatre === theatre);
  while (frontier.length > 0) {
    const here = frontier.filter((n) => n.kind === 'mission' && n.params !== null && !n.tagRecovery);
    if (here.length > 0) {
      const node = new Pcg32(op.seed, 0x0911).fork(fallen.id).pick(here);
      const marker = { name: fullName(fallen) };
      node.tagRecovery = marker;
      if (node.params) node.params.tagRecovery = { ...marker };
      op.tagNodes[node.id] = fallen.id;
      op.log.push(`${marker.name}'s tags can still be brought home.`);
      return node;
    }
    for (const n of frontier) seen.add(n.id);
    const ahead = new Set(frontier.flatMap((n) => n.links));
    frontier = [...ahead].map((id) => findNode(op, id))
      .filter((n) => n.theatre === theatre && unresolved(n) && !seen.has(n.id));
  }
  return null;
}

/**
 * A mission on a marked node has been flown. If the tags came home with a ranger who got home
 * himself, the fallen man's entry on the wall is confirmed and one of his perks (a seeded
 * choice; none if he had none) becomes trainable. Either way the marker is cleared.
 */
export function settleTags(
  profile: ProfileState, op: OperationState, node: TheatreNode, summary: FullSummary,
): Aftermath['tagRecovery'] | undefined {
  const fallenId = op.tagNodes[node.id];
  if (!node.tagRecovery && fallenId === undefined) return undefined;
  clearTagMarker(op, node);
  const home = summary.outcome === 'extracted' || summary.outcome === 'walkedOut';
  const entry = profile.memorial.find((m) => m.ranger.id === fallenId);
  if (summary.tagsRecovered !== true || !home || !entry) return undefined;
  entry.tagsRecovered = true;
  const perks = entry.ranger.perks;
  const perk: PerkId | null = perks.length > 0 ? new Pcg32(entry.ranger.seed, 0x0912).pick(perks) : null;
  const name = fullName(entry.ranger);
  if (perk) op.trainablePerks = [...(op.trainablePerks ?? []), perk];
  const lesson = perk ? ` ${PERKS.find((p) => p.id === perk)?.name ?? perk} can be taught to one ranger.` : '';
  op.log.push(`${name}'s tags came home: his medals are confirmed.${lesson}`);
  return { name, perk };
}

/** Teach one of the perks recovered tags have put on offer; it leaves the list with him. */
export function trainPerk(profile: ProfileState, rangerId: string, perk: PerkId): RangerRecord {
  const op = profile.operation;
  if (!op || (op.status !== 'active' && op.status !== 'won')) throw new Error('There is no operation under way.');
  const offered = op.trainablePerks ?? [];
  const index = offered.indexOf(perk);
  if (index < 0) throw new Error('That perk is not on offer: it takes recovered tags to teach one.');
  const ranger = companyRanger(op, rangerId);
  const present = ranger.status === 'ready' || ranger.status === 'wounded' || ranger.status === 'critical';
  if (!present) throw new Error(`${fullName(ranger)} is not with the company (${ranger.status}).`);
  if (op.deployed?.rangerId === rangerId) throw new Error(`${fullName(ranger)} is in the field.`);
  const perkName = PERKS.find((p) => p.id === perk)?.name ?? perk;
  if (ranger.perks.includes(perk)) throw new Error(`${fullName(ranger)} already has ${perkName}.`);
  ranger.perks.push(perk);
  redealChoices(ranger, perk);
  op.trainablePerks = offered.filter((_, i) => i !== index);
  op.log.push(`${fullName(ranger)} is taught ${perkName}.`);
  return ranger;
}
